<template>
  <div id="edit-profile" class="mt-5 p-5">
    <v-container>
      <v-list-item-group v-if="errors.length" class="mb-4">
        <v-alert dense text type="error">
          <p v-for="(error, i) in errors" :key="i" class="mb-0">{{ error }}</p>
        </v-alert>
      </v-list-item-group>

      <v-row>
        <v-flex class="pa-2" xs12 sm6>
          <div class="headline mb-2">Edit Account Details</div>
          <v-card outlined>
            <v-card-text>
              <div class="grey-text mt-5">
                <v-text-field
                  v-model="userData.displayName"
                  label="Full Name"
                  outlined
                >
                  <template v-slot:append-outer>
                    <v-btn color="primary" depressed @click="saveDisplayName">Save</v-btn>
                  </template>
                </v-text-field>
                <v-text-field
                  v-model="userData.email"
                  label="Email"
                  type="email"
                  outlined
                >
                  <template v-slot:append-outer>
                    <v-btn color="primary" depressed @click="saveEmail">Save</v-btn>
                  </template>
                </v-text-field>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex class="pa-2" xs12 sm6>
          <div class="headline mb-2">Change Password</div>
          <v-card outlined>
            <v-card-text>
              <div class="grey-text mt-5">
                <v-text-field
                  v-model="newPassword"
                  label="New Password"
                  :type="showNewPassword ? 'text' : 'password'"
                  :append-icon="showNewPassword ? 'visibility' : 'visibility_off'"
                  @click:append="showNewPassword = !showNewPassword"
                  outlined
                ></v-text-field>
                <v-text-field
                  v-model="confirmPassword"
                  label="Confirm Password"
                  :type="showConfirmNewPassword ? 'text' : 'password'"
                  :append-icon="showConfirmNewPassword ? 'visibility' : 'visibility_off'"
                  @click:append="showConfirmNewPassword = !showConfirmNewPassword"
                  outlined
                ></v-text-field>
              </div>
              <div>
                <v-btn
                  :disabled="!updatePasswordEnabled"
                  @click="changePassword"
                  type="submit"
                  color="primary"
                  depressed
                >
                Save Changes
              </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-row>

      <div class="headline mb-2">Delete Account</div>
      <v-card outlined>
        <v-card-text>
          <p>Type in your associated email address to permanently delete your account.</p>
          <div class="grey-text mt-5">
            <v-text-field
              v-model="confirmDelete"
              label="yourname@email.com"
              type="email"
              outlined
              color="red lighten-1"
            ></v-text-field>
          </div>
          <div>
            <v-btn
              type="submit"
              color="error"
              depressed
              :disabled="!deleteEnabled"
              @click="promptDelete"
            >Delete</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import 'vuejs-noty/dist/vuejs-noty.css';
import validate from '@/modules/validation-module';

export default {
  name: "EditProfile",
  data() {
    return {
      errors: [],
      userData: {
        displayName: null,
        email: null
      },
      confirmPassword: '',
      newPassword: '',
      confirmDelete: null,
      showNewPassword: false,
      showConfirmNewPassword: false,
    };
  },
  computed: {
    user() {
      return this.$firebase.auth().currentUser;
    },
    deleteEnabled() {
      return this.confirmDelete === this.user.email;
    },
    updatePasswordEnabled() {
      return this.newPassword === this.confirmPassword && this.newPassword.length > 8;
    },
  },
  watch: {
    user: {
      immediate: true,
      handler(val) {
        this.userData = { ...val };
      },
    },
  },
  methods: {
    promptDelete() {
      if (window.confirm("WARNING: Once an account is deleted there is no going back and a new license key will need to be purchased.")) {
        this.deleteAccount();
      }
    },
    saveDisplayName() {
      this.errors = [];
      this.user.updateProfile({ 
        displayName: this.userData.displayName, 
        email: this.userData.email,
      }).then(() => {
        this.$noty.success('Success! Your display name has been updated.');
      }).catch((error) => {
        this.errors.push(error.message);
      })
    },
    saveEmail() {
      this.errors = [];
      if (!validate.email(this.userData.email)) {
        this.errors.push('Please enter a valid email address.');
      }
      if (!this.errors.length) {
        this.user.updateEmail(this.userData.email)
          .then(() => {
            this.user.sendEmailVerification().then(() => {
              this.$noty.success('Success! Your email has been updated and a verification email has been sent to the new email on file.');
            }).catch((error) => {
              this.$noty.error(error.message);
            });
          }).catch((error) => {
            this.errors.push(error.message);
          });
      }
    },
    changePassword() {
      this.errors = [];
      if (!validate.password(this.newPassword)) {
        this.errors.push('Passwords must contain at least 4 characters, a capitalized letter, and a number.');
      }
      if (!this.errors.length) {
        this.user.updatePassword(this.newPassword).then(() => {
          this.newPassword = null;
          this.confirmPassword = null;
          this.$noty.success('Success! Your password has been reset.');
        }).catch((error) => {
          this.errors.push(error.message);
        });
      }
    },
    deleteAccount() {
      this.user.delete().then(() => {
        this.$firebase.auth().signOut().then(() => {
          const user = null;
          this.$store.dispatch('fetchUser', { user });
          this.$router.push({
            name: 'home',
          });
        });
      }).catch((error) => {
        this.errors.push(error.message);
      });
    }
  },
};
</script>